import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import type React from 'react'

export const GAP = 8
// Extra width reserved per row when editable to account for the × button expanding on hover
// (maxWidth: 16px + ml: 6px)
export const EDITABLE_EXTRA = 22
// The add button is square at 28px (intentionally larger than tag height to match design)
export const ADD_BUTTON_WIDTH = 28
// Minimum width of the inline search input — used for row-break decisions
export const SEARCH_INPUT_MIN_WIDTH = 120

/** Reads the rendered widths of all [data-tag-item] children into itemWidthsRef and returns the array. */
export function measureItemWidths(
  container: HTMLElement,
  itemWidthsRef: React.MutableRefObject<number[]>
): number[] {
  const items = Array.from(
    container.querySelectorAll('[data-tag-item]')
  ) as HTMLElement[]
  items.forEach((item, i) => {
    if (item.offsetWidth > 0) itemWidthsRef.current[i] = item.offsetWidth
  })
  return itemWidthsRef.current
}

/** Runs compute immediately, then re-runs it whenever the container resizes. Returns a cleanup function. */
export function observeResize(
  container: HTMLElement,
  compute: () => void
): () => void {
  compute()
  const observer = new ResizeObserver(compute)
  observer.observe(container)
  return () => observer.disconnect()
}

/** Assigns node to both the internal ref and the forwarded ref (function or object form). */
export function mergeRefs<T>(
  node: T,
  internalRef: React.MutableRefObject<T>,
  forwardedRef: React.ForwardedRef<T>
) {
  internalRef.current = node
  if (typeof forwardedRef === 'function') forwardedRef(node)
  else if (forwardedRef) forwardedRef.current = node
}

function computeVisibleCount(
  container: HTMLElement,
  itemWidthsRef: React.MutableRefObject<number[]>,
  setVisibleCount: (count: number | undefined) => void,
  editableExtra: number
) {
  // Guard against detached/hidden containers — same scenario as computeRowBreaks.
  if (!container.isConnected || container.offsetWidth <= 0) return
  // Reserve space for one expanded × button — only one tag can be hovered at a time
  const containerWidth = container.offsetWidth - editableExtra
  const ellipsisEl = container.querySelector(
    '[data-tag-ellipsis]'
  ) as HTMLElement | null
  const ellipsisWidth = ellipsisEl ? ellipsisEl.offsetWidth + GAP : 0

  // Store widths from visible items; hidden items (display:none) have offsetWidth 0
  // so we retain the previously measured width for those slots.
  const widths = measureItemWidths(container, itemWidthsRef)
  if (!widths.length) return

  const totalWidth = widths.reduce(
    (sum, w, i) => sum + w + (i > 0 ? GAP : 0),
    0
  )
  if (totalWidth <= containerWidth) {
    setVisibleCount(undefined)
    return
  }

  let count = 0
  let usedWidth = 0
  for (const w of widths) {
    const gap = count > 0 ? GAP : 0
    if (usedWidth + gap + w + ellipsisWidth <= containerWidth) {
      usedWidth += gap + w
      count++
    } else {
      break
    }
  }
  setVisibleCount(count)
}

function computeRowBreaks(
  container: HTMLElement,
  itemWidthsRef: React.MutableRefObject<number[]>,
  setRowBreaks: (breaks: number[]) => void,
  editableExtra: number,
  trailingItemWidth?: number
) {
  // Reserve editableExtra per row so hovering a tag never pushes the last tag on that row to wrap.
  // trailingItemWidth, when provided, is appended as a virtual last item (the add button).
  const containerWidth = container.offsetWidth
  // Guard against detached/hidden containers — a stale ResizeObserver callback on an unmounted
  // element (e.g. TagGroupNoWrap detaching when TagGroupWrap mounts) would otherwise compute
  // one break per tag and overwrite the correct layout.
  if (containerWidth <= 0 || !container.isConnected) return
  const measured = measureItemWidths(container, itemWidthsRef)

  const allWidths =
    trailingItemWidth != null ? [...measured, trailingItemWidth] : measured
  if (!allWidths.length) return

  const breaks: number[] = [0]
  let rowWidth = 0

  for (let i = 0; i < allWidths.length; i++) {
    const w = allWidths[i] ?? 0
    const gap = rowWidth > 0 ? GAP : 0
    if (rowWidth + gap + w + editableExtra <= containerWidth) {
      rowWidth += gap + w
    } else {
      if (rowWidth > 0) breaks.push(i)
      rowWidth = w
    }
  }

  setRowBreaks(breaks)
}

/**
 * Shared layout hook for TagGroup variants. Computes visibleCount (NoWrap) or
 * rowBreaks (Wrap) by measuring tag widths and observing container resize.
 */
export function useTagLayout(
  wrap: boolean,
  editable: boolean,
  childCount: number,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
  hasAddInput: boolean = false
) {
  const itemWidthsRef = useRef<number[]>([])
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const [visibleCount, setVisibleCount] = useState<number | undefined>(
    undefined
  )
  const [rowBreaks, setRowBreaks] = useState<number[]>([])
  const prevConfigRef = useRef({ childCount, wrap, editable, hasAddInput })

  // Reset visibility state during render when the number of children or config
  // changes. Calling setState during render triggers an immediate re-render
  // before any effects fire, so all tags are visible in the DOM when the layout
  // effect measures them — preventing truncation decisions based on a stale
  // width cache.
  // NOTE: we compare childCount (not children by reference) because JSX always
  // creates new element instances, which would cause a false reset on every
  // render and unmount the inline search input mid-typing.
  const hasConfigChanged =
    prevConfigRef.current.childCount !== childCount ||
    prevConfigRef.current.wrap !== wrap ||
    prevConfigRef.current.editable !== editable ||
    prevConfigRef.current.hasAddInput !== hasAddInput

  if (hasConfigChanged) {
    prevConfigRef.current = { childCount, wrap, editable, hasAddInput }
    itemWidthsRef.current = []
    if (visibleCount !== undefined) setVisibleCount(undefined)
    if (rowBreaks.length > 0) setRowBreaks([])
  }

  // Stable ref callback: stores the container in state (so the layout effect
  // re-runs when the container element changes, e.g. NoWrap ↔ Wrap swap) and
  // forwards the ref to external consumers.
  const refCallback = useCallback(
    (node: HTMLDivElement | null) => {
      setContainer(node)
      if (typeof forwardedRef === 'function') forwardedRef(node)
      else if (forwardedRef) forwardedRef.current = node
    },
    [forwardedRef]
  )

  useLayoutEffect(() => {
    if (!container) return

    const extra = editable ? EDITABLE_EXTRA : 0

    if (!wrap) {
      return observeResize(container, () =>
        computeVisibleCount(container, itemWidthsRef, setVisibleCount, extra)
      )
    }

    const trailingWidth = hasAddInput
      ? SEARCH_INPUT_MIN_WIDTH
      : editable
      ? ADD_BUTTON_WIDTH
      : undefined
    return observeResize(container, () =>
      computeRowBreaks(
        container,
        itemWidthsRef,
        setRowBreaks,
        extra,
        trailingWidth
      )
    )
  }, [container, wrap, editable, childCount, hasAddInput])

  return { visibleCount, rowBreaks, refCallback }
}
